import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, FormGroup, FormLabel, FormControl, InputGroup, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import { FiEye, FiEyeOff } from '../../../../icons';
import { motion, AnimatePresence } from "framer-motion";
import MainLogo from "../../../../images/main-logo.svg";
import LoginImage from "../../../../images/otp-image.png";
import AuthLeftSection from "../AuthLeftSection/AuthLeftSection.tsx";
import AuthBackground from "../AuthBackground/AuthBackground.tsx";
import { useNavigate } from 'react-router-dom';
import apiConfig from '../../config/apiConfig.js';
import { useFlowData } from '../hooks/useFlowData';
import { createPasswordSchema } from '../utils/validationSchema.js';
import { API_ENDPOINTS } from '../utils/constants';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./CreatePassword.scss";

function CreatePassword() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { email: userEmail, isFromForgotPassword, hasValidFlow } = useFlowData();

  // Move useFormik hook here - BEFORE any conditional logic
  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: createPasswordSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const authToken = localStorage.getItem("authToken");
        const headers: { [key: string]: string } = {
          "Content-Type": "application/json",
        };

        if (authToken) {
          headers.Authorization = `Bearer ${authToken}`;
        }

        const requestBody: {
          password: string;
          confirmPassword: string;
          email?: string;
        } = {
          password: values.newPassword,
          confirmPassword: values.confirmPassword,
        };

        if (!isFromForgotPassword) {
          requestBody.email = userEmail;
        }

        const response = await axios.post(
          `${apiConfig.apiBaseURL}${API_ENDPOINTS.CREATE_PASSWORD}`,
          requestBody,
          { headers }
        );

        if (response.data.status === "Success") {
          if (response.data.token) {
            localStorage.setItem("authToken", response.data.token);
          }
          
          toast.success(response.data.message || 'Password created successfully!');
          
          if (isFromForgotPassword) {
            localStorage.removeItem("authToken");
            localStorage.removeItem("verifiedEmail");
            navigate('/');
          } else {
            // After successful registration password creation, proceed to subscription step
            navigate('/subscription');
          }
        } else {
          toast.error(response.data.message || 'Failed to create password. Please try again.');
        }
      } catch (error) {
        const errMsg = error.response?.data?.message || 'Failed to create password. Please try again.';
        toast.error(errMsg);
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Use useEffect to handle redirects instead of early return
  useEffect(() => {
    if (!hasValidFlow || !userEmail) {
      toast.error('Please try again.');
      navigate('/');
    }
  }, [hasValidFlow, userEmail, navigate]);

  const handleValidateAndSubmit = async () => {
    const { newPassword, confirmPassword } = formik.values;

    if (!newPassword || !confirmPassword) {
      // Just mark fields as touched to show validation errors below fields
      // No need for toast as Formik already shows errors below each field
      formik.setTouched({ newPassword: true, confirmPassword: true });
      return;
    }

    const errors = await formik.validateForm();

    if (Object.keys(errors).length > 0) {
      // Validation errors are already displayed inline below the fields
      // No need for toast messages here
      formik.setTouched({ newPassword: true, confirmPassword: true });
      return;
    }

    formik.handleSubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
    }
  };

  // Return null if invalid flow, but after all hooks have been called
  if (!hasValidFlow || !userEmail) {
    return null;
  }

  // Animation variants for smooth, calm transitions (same as OTP)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.2
      }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -60, scale: 0.95 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 1,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 60, scale: 0.95 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 1,
        ease: [0.25, 0.46, 0.45, 0.94],
        delay: 0.3
      }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const floatingAnimation = {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const buttonHover = {
    scale: 1.02,
    transition: { duration: 0.2, ease: "easeInOut" }
  };

  const inputFocus = {
    scale: 1.01,
    boxShadow: "0 8px 30px rgba(57, 87, 160, 0.12)",
    transition: { duration: 0.3, ease: "easeOut" }
  };

  return (
    <>
      {/* Full-screen background animations */}
      <AuthBackground />

      <motion.section
        className="login-screen"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 15px', position: 'relative', zIndex: 1 }}
      >
        <div style={{ maxWidth: '1600px', width: '100%' }}>
        <div className="row" style={{ width: '100%', margin: '0' }}>
        {/* Left Panel - Enhanced Image and Content */}
        <AuthLeftSection
          image={LoginImage}
          imageAlt="Create Password"
          title="Giving Presence to Future Moments"
          description="We’re more than a service—it’s a movement to redefine how love endures."
          slideInLeft={slideInLeft}
          fadeInUp={fadeInUp}
          floatingAnimation={floatingAnimation}
        />

        {/* Right Panel - Password Form */}
        <div className="col-md-6" style={{ display: 'flex' }}>
          {/* OUTER WRAPPER - Has background and padding */}
          <motion.div
            className="login-screen-right-wrapper"
            variants={slideInRight}
            initial="hidden"
            animate="visible"
            style={{
              height: 'calc(100vh - 20px)',
              width: '100%',
              background: '#F2F3F7',
              borderRadius: '24px'
            }}
          >
            {/* INNER SCROLLABLE CONTAINER */}
            <div
              className="login-screen-right pt-5"
              style={{
                height: '100%',
                overflowY: 'auto',
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div className="login-screen-right-in" style={{ background: 'transparent' }}>
            <motion.div
              className="middle-block"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                ease: "easeOut",
                delay: 0.6
              }}
            >
              <motion.figure
                className="main-logo"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  duration: 0.8,
                  ease: "easeOut",
                  delay: 0.8
                }}
                whileHover={{ scale: 1.05 }}
              >
                <img src={MainLogo} alt="Main Logo" />
              </motion.figure>

              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  ease: "easeOut",
                  delay: 1
                }}
                style={{ marginBottom: '2rem', color: '#2c3e50' }}
              >
                {isFromForgotPassword ? 'Reset Your Password' : 'Create Your Password'}
              </motion.h3>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.6 }}
              >
                <Form onSubmit={formik.handleSubmit} className='pt-0'>
                  {/* New Password */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.3, duration: 0.6 }}
                  >
                    <FormGroup className="form-group password-field">
                      <FormLabel>New Password *</FormLabel>
                      <motion.div whileFocus={inputFocus}>
                        <InputGroup>
                          <FormControl
                            autoFocus
                            type={showPassword ? 'text' : 'password'}
                            name="newPassword"
                            placeholder="Password"
                            value={formik.values.newPassword}
                            onChange={formik.handleChange}
                            className={`form-control p-3`}
                            autoComplete="new-password"
                            onKeyDown={handleKeyDown}
                            disabled={formik.isSubmitting}
                            style={{
                              border: '2px solid #e0e6ed',
                              borderRadius: '12px 0 0 12px',
                              transition: 'all 0.3s ease'
                            }}
                          />
                          <InputGroup.Text
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              cursor: "pointer",
                              userSelect: "none",
                              border: '2px solid #e0e6ed',
                              borderLeft: 'none',
                              borderRadius: '0 12px 12px 0',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {showPassword ? (
                              <FiEyeOff size={20} />
                            ) : (
                              <FiEye size={20} />
                            )}
                          </InputGroup.Text>
                        </InputGroup>
                      </motion.div>
                      <AnimatePresence>
                        {formik.touched.newPassword && formik.errors.newPassword && (
                          <motion.small
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{
                              color: '#dc3545',
                              display: 'block',
                              marginTop: '8px',
                              fontSize: '0.875rem'
                            }}
                          >
                            {formik.errors.newPassword}
                          </motion.small>
                        )}
                      </AnimatePresence>
                    </FormGroup>
                  </motion.div>

                  {/* Confirm Password */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.4, duration: 0.6 }}
                  >
                    <FormGroup className="form-group password-field">
                      <FormLabel>Confirm New Password *</FormLabel>
                      <motion.div whileFocus={inputFocus}>
                        <InputGroup>
                          <FormControl
                            type={showConfirmPassword ? 'text' : 'password'}
                            name="confirmPassword"
                            placeholder="Confirm Password"
                            value={formik.values.confirmPassword}
                            onChange={formik.handleChange}
                            className={`form-control p-3`}
                            autoComplete="new-password"
                            onKeyDown={handleKeyDown}
                            onPaste={(e) => e.preventDefault()}
                            disabled={formik.isSubmitting}
                            style={{
                              border: '2px solid #e0e6ed',
                              borderRadius: '12px 0 0 12px',
                              transition: 'all 0.3s ease'
                            }}
                          />
                          <InputGroup.Text
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            style={{
                              cursor: "pointer",
                              userSelect: "none",
                              border: '2px solid #e0e6ed',
                              borderLeft: 'none',
                              borderRadius: '0 12px 12px 0',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {showConfirmPassword ? (
                              <FiEyeOff size={20} />
                            ) : (
                              <FiEye size={20} />
                            )}
                          </InputGroup.Text>
                        </InputGroup>
                      </motion.div>
                      <AnimatePresence>
                        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                          <motion.small
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{
                              color: '#dc3545',
                              display: 'block',
                              marginTop: '8px',
                              fontSize: '0.875rem'
                            }}
                          >
                            {formik.errors.confirmPassword}
                          </motion.small>
                        )}
                      </AnimatePresence>
                    </FormGroup>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.6, duration: 0.6 }}
                    whileHover={buttonHover}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      className="main-bttn"
                      type="submit"
                      onClick={handleValidateAndSubmit}
                      disabled={formik.isSubmitting}
                      style={{
                        width: '100%',
                        padding: '14px 20px',
                        background: formik.isSubmitting
                          ? 'linear-gradient(135deg, #bbb, #999)'
                          : '#D7A360',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        boxShadow: '0 8px 20px rgba(215, 163, 96, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <AnimatePresence mode="wait">
                        {formik.isSubmitting ? (
                          <motion.span
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <motion.span
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              style={{ display: 'inline-block', marginRight: '8px' }}
                            >
                              ⟳
                            </motion.span>
                            {isFromForgotPassword ? 'Resetting Password...' : 'Creating Password...'}
                          </motion.span>
                        ) : (
                          <motion.span
                            key="submit"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            {isFromForgotPassword ? 'Reset Password' : 'Create Password'}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </Button>
                  </motion.div>

                  <motion.p
                    className="otp-text"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.8, duration: 0.6 }}
                    style={{
                      fontSize: "16px",
                      textAlign: "left",
                      marginTop: '1.5rem',
                      color: '#677289',
                      lineHeight: '1.6'
                    }}
                  >
                    <strong style={{ color: '#2c3e50' }}>Note:</strong><br />
                    Minimum length: 8 characters<br />
                    Include at least one uppercase letter, one lowercase letter,<br />
                    one number, and one special character (!, @, #, etc.)<br />
                    Avoid common words, phrases, or predictable patterns
                  </motion.p>
                </Form>
              </motion.div>
            </motion.div>
          </div>
          </div>
          </motion.div>
        </div>
      </div>
      </div>
      </motion.section>
    </>
  );
}

export default CreatePassword;